import { Autocomplete, Card, Collapse, MenuItem, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import { useFormikContext } from "formik";
import { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import useGetAllCategories from "@/hooks/requests/category/use-get-all-categories";
import { IOrder } from "@/interfaces/order.interfaces";
import { OrdersFiltersMessages } from "@/messages/orders/order-filters.messages";
import { FilterType, FilterValue, IFilter } from "@/types/filter.types";

import { ORDER_STATUS } from "../../constants";

type Props = {
  messages: OrdersFiltersMessages;
};

type OrderFilterProperty = keyof IOrder;
// Extendendo o tipo para incluir propriedades virtuais para filtro de período e categoria
type OrderFilterPropertyExtended = OrderFilterProperty | "startDate" | "endDate" | "category.id";

interface IOrderFilter {
  property: OrderFilterPropertyExtended;
  value: FilterValue;
  filterType: FilterType;
  label?: string;
}

function FiltersForm({ messages }: Props) {
  const { values, setValues, submitForm } = useFormikContext<IOrderFilter[]>();
  const [expanded, setExpanded] = useState<boolean>(false);
  const { data: categories } = useGetAllCategories();

  const handleCategoryChange = (_: unknown, newValue: { id: number; description: string } | null) => {
    handleChange("category.id", newValue ? newValue.id : "");
  };

  const getSelectedCategory = () => {
    const categoryId = getValueFromProperty("category.id");
    if (!categoryId) return null;
    return categories.find((cat) => cat.id === Number(categoryId)) || null;
  };

  const handleChange = (property: OrderFilterPropertyExtended, value: FilterValue) => {
    const newValue = value;

    // Find if a filter for this property already exists
    const existingFilterIndex = values.findIndex((filter) => filter.property === property);

    let updatedValues: IOrderFilter[];

    if (existingFilterIndex >= 0) {
      // Update the existing filter
      updatedValues = [...values];
      updatedValues[existingFilterIndex] = {
        ...updatedValues[existingFilterIndex],
        value: newValue,
      };
    } else {
      // Add a new filter while keeping the existing ones
      const filterConfig = getFilterConfig(property);
      updatedValues = [
        ...values,
        {
          property,
          value: newValue,
          filterType: filterConfig.filterType,
          label: filterConfig.label,
        },
      ];
    }

    setValues(updatedValues);
  };

  const getFilterConfig = (property: OrderFilterPropertyExtended) => {
    const configs = {
      orderNumber: {
        filterType: FilterType.Equals,
        label: messages.orderNumberOption,
        transform: undefined,
      },
      billingName: {
        filterType: FilterType.Contains,
        label: messages.buyerOption,
        transform: undefined,
      },
      salesChannel: {
        filterType: FilterType.Contains,
        label: messages.salesChannelOption,
        transform: undefined,
      },
      startDate: {
        filterType: FilterType.GreaterThan,
        label: messages.startDate,
        transform: (value: string) => dayjs(value).format("YYYY-MM-DD 00:00:00"),
      },
      endDate: {
        filterType: FilterType.LessThan,
        label: messages.endDate,
        transform: (value: string) => dayjs(value).format("YYYY-MM-DD 23:59:59"),
      },
      sku: {
        filterType: FilterType.Contains,
        label: messages.skuOption,
        transform: undefined,
      },
      orderStatus: {
        filterType: FilterType.Equals,
        label: messages.statusOption,
        transform: undefined,
      },
      "category.id": {
        filterType: FilterType.Equals,
        label: messages.categoryOption,
        transform: undefined,
      },
    };

    return (
      configs[property as keyof typeof configs] || {
        filterType: FilterType.Contains,
        label: "",
        transform: undefined,
      }
    );
  };


  const handleSubmit = () => {
    const formattedFilters: IFilter<IOrder>[] = [];
    values.forEach((filter) => {
      if (filter.value) {
        const config = getFilterConfig(filter.property);
        const transformedValue = config.transform
          ? config.transform(String(filter.value))
          : filter.value;
        const apiProperty =
          filter.property === "startDate" || filter.property === "endDate"
            ? "orderDate"
            : filter.property;
        formattedFilters.push({
          property: apiProperty as keyof IOrder,
          value: transformedValue,
          filterType: filter.filterType,
          label: filter.label,
        } as IFilter<IOrder>);
      }
    });
    submitForm();
    setValues(formattedFilters as IOrderFilter[]);
  };

  const handleClear = () => {
    setValues([]);
    submitForm();
  };

  const getValueFromProperty = (property: OrderFilterPropertyExtended) => {
    const filter = values.find((filter) => filter.property === property);
    return filter ? String(filter.value) : "";
  };

  const statusOptions = ORDER_STATUS(messages);

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={3}>
          <Grid size={{ lg: 4, md: 4, sm: 6, xs: 12 }}>
            <TextField
              fullWidth
              type="text"
              label={messages.orderNumberOption}
              value={getValueFromProperty("orderNumber")}
              onChange={(e) => handleChange("orderNumber", e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>

          <Grid size={{ lg: 4, md: 4, sm: 6, xs: 12 }}>
            <TextField
              fullWidth
              label={messages.buyerOption}
              value={getValueFromProperty("billingName")}
              onChange={(e) => handleChange("billingName", e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>

          <Grid size={{ lg: 4, md: 4, sm: 6, xs: 12 }}>
            <TextField
              fullWidth
              label={messages.salesChannelOption}
              value={getValueFromProperty("salesChannel")}
              onChange={(e) => handleChange("salesChannel", e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>

          <Grid size={12}>
            <Collapse in={expanded} unmountOnExit>
              <Grid container spacing={3}>
                <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label={messages.startDate}
                    value={getValueFromProperty("startDate")}
                    onChange={(e) => handleChange("startDate", e.target.value)}
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  />
                </Grid>

                <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label={messages.endDate}
                    value={getValueFromProperty("endDate")}
                    onChange={(e) => handleChange("endDate", e.target.value)}
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  />
                </Grid>

                <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
                  <TextField
                    fullWidth
                    label={messages.skuOption}
                    value={getValueFromProperty("sku")}
                    onChange={(e) => handleChange("sku", e.target.value)}
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  />
                </Grid>

                <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
                  <Autocomplete
                    options={categories}
                    getOptionLabel={(option) => option.description}
                    value={getSelectedCategory()}
                    onChange={handleCategoryChange}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={messages.categoryOption}
                        slotProps={{
                          inputLabel: { shrink: true },
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: '0.25rem !important',
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
                  <TextField
                    select
                    fullWidth
                    label={messages.statusOption}
                    value={getValueFromProperty("orderStatus")}
                    onChange={(e) => handleChange("orderStatus", e.target.value)}
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Collapse>

            <Grid size={12} container spacing={2} mt={expanded ? 2 : -1}>
              <Grid size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={() => setExpanded((prev) => !prev)}
                  fullWidth
                >
                  {expanded ? messages.lessFilters : messages.moreFilters}
                </MDButton>
              </Grid>
              <Grid size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                <MDButton variant="gradient" color="light" onClick={handleClear} fullWidth>
                  {messages.clearButton}
                </MDButton>
              </Grid>
              <Grid size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                <MDButton variant="gradient" color="dark" onClick={handleSubmit} fullWidth>
                  {messages.filterButton}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default FiltersForm;
